import { CalendarDays, Clock3, Mail, ShieldCheck, ShieldQuestion } from "lucide-react";
import type { Role } from "@/lib/users.api";
import { cn } from "@/lib/utils";
import { AccountStatusBadge } from "./AccountStatusBadge";
import { RolesBadge } from "./RolesBadges";

const ROLE_LABELS: Record<Role, string> = {
  agent: "Agent",
  manager: "Manager",
  admin: "Admin",
  super_admin: "Super Admin",
  consultant: "Consultant",
  dev: "Dev / Supervision",
};

function initials(name: string): string {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}

function fmtDate(value: string): string {
  return new Date(value).toLocaleDateString("fr-FR");
}

// Identity fields are optional: /profile self-view for non-admin roles only
// has what the JWT carries (id/fullName/email/role), and the admin-facing
// reporting page intentionally withholds this whole object from managers
// (see EmployeeProfileHeaderRestricted below). Anything not provided is
// simply omitted rather than faked.
export interface EmployeeProfileHeaderData {
  id: number;
  fullName: string;
  email: string;
  role: Role;
  active?: boolean;
  firstLogin?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface EmployeeProfileHeaderProps {
  employee: EmployeeProfileHeaderData;
  subtitleLabel?: string;
  periodLabel?: string;
  lastActivityLabel?: string;
}

export function EmployeeProfileHeader({
  employee,
  subtitleLabel,
  periodLabel,
  lastActivityLabel,
}: EmployeeProfileHeaderProps) {
  const infoItems = [
    { label: "Email", value: employee.email, icon: Mail },
    { label: "Type", value: ROLE_LABELS[employee.role], icon: ShieldCheck },
    employee.createdAt
      ? { label: "Créé le", value: fmtDate(employee.createdAt), icon: CalendarDays }
      : null,
    employee.updatedAt
      ? { label: "Mis à jour", value: fmtDate(employee.updatedAt), icon: Clock3 }
      : null,
  ].filter((item): item is { label: string; value: string; icon: typeof Mail } => item !== null);

  // Fewer than 3 fields (self-view for non-admin roles, which never gets
  // createdAt/updatedAt) shouldn't stretch across the same wide column the
  // full 4-item admin grid uses — that leaves a visibly empty right-hand
  // zone. Let the info block size to its content instead.
  const compact = infoItems.length <= 2;

  return (
    <section className="mb-6 rounded-lg border border-border bg-card px-5 py-5">
      <div
        className={cn(
          "grid gap-5 lg:items-start",
          compact
            ? "lg:grid-cols-[minmax(260px,1fr)_auto]"
            : "lg:grid-cols-[minmax(260px,1fr)_minmax(420px,1.7fr)]",
        )}
      >
        <div className="flex gap-4">
          <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-lg border border-border bg-surface-muted text-[22px] font-bold text-body">
            {initials(employee.fullName)}
          </div>
          <div className="min-w-0">
            <p className="text-[22px] font-semibold leading-tight text-foreground">
              {employee.fullName}
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11.5px] text-muted-foreground">
              <span>#{employee.id}</span>
              {subtitleLabel && (
                <>
                  <span>·</span>
                  <span>{subtitleLabel}</span>
                </>
              )}
              {lastActivityLabel && (
                <>
                  <span>·</span>
                  <span>{lastActivityLabel}</span>
                </>
              )}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-1.5">
              <RolesBadge role={employee.role} />
              {employee.active !== undefined && employee.firstLogin !== undefined && (
                <AccountStatusBadge active={employee.active} firstLogin={employee.firstLogin} />
              )}
              {periodLabel && (
                <span className="inline-flex items-center rounded px-2 py-0.5 text-[10.5px] font-semibold bg-brand-gold-light/30 text-brand-gold-dark">
                  {periodLabel}
                </span>
              )}
            </div>
          </div>
        </div>

        <div
          className={cn(
            "grid gap-x-5 gap-y-3",
            compact ? "grid-cols-2 sm:w-fit sm:min-w-80" : "sm:grid-cols-2",
          )}
        >
          {infoItems.map((item) => {
            const Icon = item.icon;
            return (
              <div key={item.label} className="min-w-0 border-l border-border pl-3">
                <p className="flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-wide text-subtle">
                  <Icon size={11} />
                  {item.label}
                </p>
                <p className="mt-1 truncate text-[12.5px] text-body">{item.value}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// Neutral stand-in when the viewer is authorized to see activity data but
// not identity data (manager viewing another employee's detail page:
// canReadEmployeeProfile is admin/super_admin only). Keeps the same visual
// shape as the real header instead of a plain paragraph, without exposing
// name/email/created/updated to a role that isn't allowed to see them.
export function EmployeeProfileHeaderRestricted({
  agentId,
  periodLabel,
}: {
  agentId?: number;
  periodLabel?: string;
}) {
  return (
    <section className="mb-6 rounded-lg border border-border bg-card px-5 py-5">
      <div className="flex gap-4">
        <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-lg border border-dashed border-border bg-surface-muted text-subtle">
          <ShieldQuestion size={26} />
        </div>
        <div className="min-w-0">
          <p className="text-[16px] font-semibold leading-tight text-foreground">
            Employé #{agentId ?? "-"}
          </p>
          <p className="mt-1 text-[11.5px] text-muted-foreground">
            Identité masquée pour votre rôle — activité affichée ci-dessous.
          </p>
          {periodLabel && (
            <div className="mt-3">
              <span className="inline-flex items-center rounded px-2 py-0.5 text-[10.5px] font-semibold bg-brand-gold-light/30 text-brand-gold-dark">
                {periodLabel}
              </span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
